import type { DailyMode, SessionKind, SessionTemplate, TrainingProgram, WorkoutExerciseResult, WorkoutRecord } from '../types'

export interface Readiness {
  energy: number
  knee: number
  heavyDay: boolean
}

export interface DailyRecommendation {
  mode: DailyMode
  reason: string
}

const strengthKinds = new Set<SessionKind>(['A', 'B', 'C', 'CHECK'])

export function recommendDailyMode(
  scheduledKind: SessionKind,
  readiness: Readiness,
  workouts: WorkoutRecord[],
  results: WorkoutExerciseResult[]
): DailyRecommendation {
  if (readiness.knee >= 5) return { mode: 'rest', reason: 'Колено сегодня заметно беспокоит — силовую лучше отложить.' }
  if (readiness.energy <= 3) return { mode: 'rest', reason: 'Энергии мало: восстановление сегодня полезнее нагрузки.' }
  if (scheduledKind === 'REST') return { mode: 'rest', reason: 'Сегодня плановый отдых. Он считается частью программы.' }
  if (readiness.heavyDay) return { mode: 'light', reason: 'Физическая работа уже дала нагрузку — оставляем короткий формат.' }
  if (readiness.energy <= 5) return { mode: 'light', reason: 'По самочувствию лучше сократить объём, но сохранить привычку.' }

  const completed = workouts.filter((item) => !item.skipped && item.actualSession !== 'REST')
  const last = completed[0]
  if (last && ((last.overallDifficulty ?? 0) >= 5 || (last.energyBefore != null && last.energyAfter != null && last.energyAfter < last.energyBefore))) {
    return { mode: 'light', reason: 'Последняя тренировка была предельной или снизила энергию — сегодня без прибавки.' }
  }

  const recentStrength = completed.filter((item) => strengthKinds.has(item.actualSession)).slice(0, 2)
  if (readiness.energy >= 8 && recentStrength.length === 2) {
    const recentIds = new Set(recentStrength.map((item) => item.id))
    const recentResults = results.filter((item) => recentIds.has(item.workoutId) && !item.skipped && item.difficulty != null)
    const controlled = recentResults.length > 0 && recentResults.every((item) => (item.rir ?? -1) >= 2 && item.pain === 0)
    const recovered = recentStrength.every((item) => (item.energyAfter ?? item.energyBefore ?? 0) >= (item.energyBefore ?? 0))
    if (controlled && recovered) return { mode: 'boosted', reason: 'Две тренировки прошли с запасом и без ухудшения самочувствия.' }
  }

  return { mode: 'standard', reason: 'Обычный объём подходит текущему восстановлению.' }
}

export function buildSessionForMode(
  mode: Exclude<DailyMode, 'rest'>,
  planned: SessionTemplate | undefined,
  program: TrainingProgram
): SessionTemplate {
  const minimal = program.sessions.find((item) => item.id === 'MIN') ?? program.sessions[0]
  const recovery = program.sessions.find((item) => item.kind === 'B' || item.kind === 'RECOVERY') ?? minimal
  const base = planned ?? recovery
  const library = new Map(program.exerciseLibrary.map((item) => [item.id, item]))

  if (mode === 'light') {
    const lightBase = base.kind === 'RECOVERY' && base.id !== 'ARMY_PREP' ? base : minimal
    return {
      ...lightBase,
      id: `${base.id}:light`,
      name: 'Лёгкая · разминка сверху вниз',
      subtitle: 'Восемь минут без силового объёма',
      durationMin: Math.min(8, lightBase.durationMin),
      exercises: lightBase.exercises
    }
  }
  if (mode === 'standard') return { ...base, id: `${base.id}:standard` }

  const strengthIndex = base.exercises.findIndex((item) => !item.optional && item.unit === 'reps' && library.get(item.exerciseId)?.category === 'strength')
  if (strengthIndex < 0) return { ...base, id: `${base.id}:boosted`, name: `${base.name} · полный вариант` }

  return {
    ...base,
    id: `${base.id}:boosted`,
    name: `${base.name} · с прибавкой`,
    subtitle: 'Одна небольшая прибавка, остальные движения без изменений',
    durationMin: base.durationMin + 2,
    exercises: base.exercises.map((item, index) => index !== strengthIndex ? item : {
      ...item,
      sets: base.format === 'circuit'
        ? item.sets.map((value, setIndex) => setIndex === 0 ? value + 1 : value)
        : [...item.sets, item.sets.at(-1) ?? 1]
    })
  }
}

export const dailyModeLabels: Record<DailyMode, { title: string; detail: string }> = {
  rest: { title: 'Отдых', detail: 'Восстановиться без чувства пропуска' },
  light: { title: 'Лёгкая', detail: '8 минут, разминка сверху вниз' },
  standard: { title: 'Обычная', detail: 'Плановый объём на сегодня' },
  boosted: { title: 'Развивающая', detail: 'Одна прибавка, не до отказа' }
}
